/**
 * Schema.org JSON-LD helper functions for AEO (Agent Experience Optimization)
 */

export function createFAQSchema(faqs: { question: string; answer: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer,
      },
    })),
  };
}

export function createBreadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url,
    })),
  };
}

export function createServiceSchema(options: {
  name: string;
  description: string;
  url: string;
  provider?: string;
  providerUrl?: string;
  category?: string;
  areaServed?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": options.name,
    "description": options.description,
    "url": options.url,
    "provider": {
      "@type": "Organization",
      "name": options.provider || "AI ToolHub",
      "url": options.providerUrl || "https://aitoolhub.ai",
    },
    ...(options.category && { "serviceType": options.category }),
    "areaServed": options.areaServed || "Worldwide",
  };
}

export function createProductSchema(options: {
  name: string;
  description: string;
  url: string;
  image?: string;
  price?: number;
  priceCurrency?: string;
  ratingValue?: number;
  reviewCount?: number;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": options.name,
    "description": options.description,
    "url": options.url,
    "image": options.image || "https://aitoolhub.ai/favicon.png",
    ...(options.price !== undefined && {
      "offers": {
        "@type": "Offer",
        "price": options.price,
        "priceCurrency": options.priceCurrency || "USD",
        "availability": "https://schema.org/OnlineOnly",
      },
    }),
    ...(options.ratingValue && options.reviewCount && {
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": options.ratingValue,
        "bestRating": 5,
        "reviewCount": options.reviewCount,
      },
    }),
  };
}

export function createOfferSchema(options: {
  name: string;
  description: string;
  price: number | string;
  priceCurrency?: string;
  url: string;
  availability?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Offer",
    "name": options.name,
    "description": options.description,
    "price": options.price,
    "priceCurrency": options.priceCurrency || "USD",
    "url": options.url,
    "availability": options.availability || "https://schema.org/OnlineOnly",
  };
}

export function createHowToSchema(options: {
  name: string;
  description: string;
  steps: { name: string; text: string }[];
  totalTime?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": options.name,
    "description": options.description,
    ...(options.totalTime && { "totalTime": options.totalTime }),
    "step": options.steps.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": step.name,
      "text": step.text,
    })),
  };
}

export function createOfferCatalogSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "OfferCatalog",
    "name": "AI ToolHub Services",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "AI Tool Directory",
          "description": "Browse and search 500+ curated AI tools with expert reviews and trust scores",
        },
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Tool Comparison & Battles",
          "description": "Compare AI tools side-by-side and vote in weekly community tool battles",
        },
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Vibe Coders Marketplace",
          "description": "Hire verified AI developers and specialists for your projects",
        },
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Digital Products Store",
          "description": "Buy and sell AI prompt packs, templates, courses, and ebooks",
        },
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Tool Listing & Verification",
          "description": "Submit your AI tool to reach 20K+ builders with verified badge options from $29/month",
        },
      },
    ],
  };
}
